// ── Lobby: create/join game screens ──

export interface LobbyCallbacks {
  onCreateGame: (name: string) => void;
  onJoinGame: (code: string, name: string) => void;
  onViewRules: () => void;
}

export function renderLobby(
  container: HTMLElement,
  callbacks: LobbyCallbacks,
): void {
  container.innerHTML = '';

  const lobby = document.createElement('div');
  lobby.className = 'lobby';

  lobby.innerHTML = `
    <div class="lobby-header">
      <h1 class="lobby-title">LACUNA</h1>
      <p class="lobby-subtitle">A game of planar construction and enclosure</p>
    </div>

    <div class="lobby-actions">
      <div class="lobby-card">
        <h2>New Game</h2>
        <input type="text" class="input-name" id="create-name" placeholder="Your name" maxlength="20" autocomplete="off" inputmode="text" enterkeyhint="go" />
        <button class="btn btn-primary" id="btn-create">Create Game</button>
      </div>

      <div class="lobby-divider"><span>or</span></div>

      <div class="lobby-card">
        <h2>Join Game</h2>
        <input type="text" class="input-name" id="join-name" placeholder="Your name" maxlength="20" autocomplete="off" inputmode="text" enterkeyhint="go" />
        <input type="text" class="input-code" id="join-code" placeholder="Room code" maxlength="4" autocomplete="off" inputmode="text" enterkeyhint="go" />
        <button class="btn btn-secondary" id="btn-join">Join</button>
      </div>
    </div>

    <button class="btn btn-link" id="btn-rules">How to Play</button>
  `;

  container.appendChild(lobby);

  // Event listeners
  const createBtn = lobby.querySelector('#btn-create') as HTMLButtonElement;
  const joinBtn = lobby.querySelector('#btn-join') as HTMLButtonElement;
  const rulesBtn = lobby.querySelector('#btn-rules') as HTMLButtonElement;
  const createName = lobby.querySelector('#create-name') as HTMLInputElement;
  const joinName = lobby.querySelector('#join-name') as HTMLInputElement;
  const joinCode = lobby.querySelector('#join-code') as HTMLInputElement;

  // Auto-uppercase room code
  joinCode.addEventListener('input', () => {
    joinCode.value = joinCode.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  });

  createBtn.addEventListener('click', () => {
    const name = createName.value.trim() || 'Player 1';
    callbacks.onCreateGame(name);
  });

  joinBtn.addEventListener('click', () => {
    const name = joinName.value.trim() || 'Player 2';
    const code = joinCode.value.trim();
    if (code.length !== 4) {
      joinCode.classList.add('input-error');
      setTimeout(() => joinCode.classList.remove('input-error'), 600);
      return;
    }
    callbacks.onJoinGame(code, name);
  });

  rulesBtn.addEventListener('click', () => callbacks.onViewRules());

  // Enter key support
  createName.addEventListener('keydown', e => {
    if (e.key === 'Enter') createBtn.click();
  });
  joinCode.addEventListener('keydown', e => {
    if (e.key === 'Enter') joinBtn.click();
  });
}

/** Show the waiting screen after creating a game */
export function renderWaiting(
  container: HTMLElement,
  roomCode: string,
  gameId: string,
): void {
  container.innerHTML = '';

  const waiting = document.createElement('div');
  waiting.className = 'lobby waiting';

  const shareUrl = `${window.location.origin}${window.location.pathname}#/g/${gameId}`;

  waiting.innerHTML = `
    <div class="lobby-header">
      <h1 class="lobby-title">LACUNA</h1>
      <p class="lobby-subtitle">Waiting for opponent&hellip;</p>
    </div>

    <div class="waiting-card">
      <div class="room-code-label">Room Code</div>
      <div class="room-code">${roomCode}</div>
      <div class="share-link">
        <input type="text" readonly value="${shareUrl}" id="share-url" />
        <button class="btn btn-small" id="btn-copy">Copy</button>
      </div>
    </div>

    <div class="waiting-spinner"></div>
  `;

  container.appendChild(waiting);

  const copyBtn = waiting.querySelector('#btn-copy') as HTMLButtonElement;
  const shareInput = waiting.querySelector('#share-url') as HTMLInputElement;

  copyBtn.addEventListener('click', () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      copyBtn.textContent = 'Copied!';
      setTimeout(() => (copyBtn.textContent = 'Copy'), 2000);
    });
  });

  shareInput.addEventListener('click', () => shareInput.select());
}
